import axios from "axios";

/**
 * Thin client for the CPT-recommender service (compose / analyze / recommend).
 *
 * Separate from RemotingService: different base URL (REACT_APP_RECOMMENDER_URL)
 * and `auth-key` auth instead of the staging OAuth bearer. In dev that base URL
 * is the same-origin "/recommender" path that setupProxy.js forwards to the
 * recommender, injecting auth-key server-side (so the token stays out of the
 * bundle). Set REACT_APP_RECOMMENDER_AUTH_KEY only when calling the recommender
 * directly (cross-origin) instead. Callback style mirrors RemotingService.
 */
const BASE_URL = process.env.REACT_APP_RECOMMENDER_URL;
const AUTH_KEY = process.env.REACT_APP_RECOMMENDER_AUTH_KEY;

const instance = axios.create({
    baseURL: BASE_URL,
    headers: AUTH_KEY ? {"auth-key": AUTH_KEY} : {},
});

class RecommenderService {

    // Free clinician text -> validated structured EMR fields + assembled emr_json.
    // body: { text, record?, visitId? }
    compose = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/emr/compose", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    // Claim-scrubber: EMR json -> {clean_claim_score, blocking_count, checked,
    // codes_checked, findings, semantic}. Pass semantic:false for the deterministic-only
    // pass (no LLM call, instant) used by the "Check EMR" badge on visit load.
    // body: { record, visitId?, codes?, maxQuestions?, semantic? }
    analyze = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/emr/analyze", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    // EMR json -> CPT/ICD recommendations (or {unavailable, required_fields} when the
    // visit lacks the inputs the recommender needs).
    // body: { record, visitId? }
    recommend = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/emr/recommend", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    // One recorded audio segment -> {text}. Whisper runs behind the recommender
    // (the service holds the speech key; the browser never sees it). 503 means the
    // server has no transcription configured — Dictation falls back to Web Speech.
    transcribe = (blob, successCallback = () => {}, failCallback = () => {}) => {
        const form = new FormData();
        form.append("audio", blob, "chunk.webm");
        instance.post("/transcribe", form)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };
}

export default new RecommenderService();
